import { times } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";

type ResourceCardProps = {
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  dataLoc?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
};

export const ResourceCard: React.FC<ResourceCardProps> = ({
  children,
  onClick,
  dataLoc,
  title,
  description,
}) => {
  return (
    <div
      className="group/resource-card flex h-56 flex-col justify-between gap-y-5 rounded-lg bg-white px-6 py-5 shadow-sm"
      data-loc={dataLoc}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
    >
      <div data-loc={dataLoc ? `${dataLoc}-body` : undefined}>
        <div
          aria-level={3}
          className="mb-2 line-clamp-3 text-gray-800 font-inter-semibold-14px"
          data-loc={dataLoc ? `${dataLoc}-title` : undefined}
          role="heading"
        >
          {title}
        </div>
        <div className="line-clamp-4 whitespace-pre-line text-gray-500 font-inter-normal-12px">
          {description}
        </div>
      </div>
      {children && <div>{children}</div>}
    </div>
  );
};

type ResourceCardsContainerProps = {
  children: React.ReactNode;
  placement?: "start" | "center";
};

export const RESOURCE_CARDS_GRID_CLASS =
  `grid grid-cols-[repeat(auto-fill,_280px)] justify-start gap-4.5` as const;

export const ResourceCardsContainer: React.FC<ResourceCardsContainerProps> = ({
  placement = "start",
  children,
}) => {
  return (
    <div
      className={twJoin(
        RESOURCE_CARDS_GRID_CLASS,
        placement === "center" && "justify-center",
      )}
    >
      {children}
    </div>
  );
};

export const ResourceCardSkeleton: React.FC = () => {
  return (
    <ResourceCard
      description={
        <div className="flex flex-col gap-y-1.5">
          {times(4, (i) => (
            <SkeletonPlaceholder
              key={i}
              height="h-3.5"
              width={i === 3 ? "w-3/4" : "w-full"}
            />
          ))}
        </div>
      }
      title={<SkeletonPlaceholder height="h-5" width="w-2/3" />}
    >
      <div className="mt-auto flex justify-between">
        <SkeletonPlaceholder height="h-5" width="w-12" />
        <SkeletonPlaceholder height="h-5" width="w-5" />
      </div>
    </ResourceCard>
  );
};
